import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Cookie, Shield, Settings, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  CONSENT_KEY,
  type ConsentPreferences,
  dispatchConsentUpdate,
} from "@/lib/consent";
import { applyConsentPreferences } from "@/lib/analytics";
const CONSENT_VERSION = "1.0";

export function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<ConsentPreferences>({
    necessary: true, // Always required
    analytics: false,
    marketing: false,
    preferences: false,
  });

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) {
      // Show banner after a slight delay
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
    try {
      const parsed = JSON.parse(stored);
      if (parsed.version !== CONSENT_VERSION) {
        // Version changed — ask again
        localStorage.removeItem(CONSENT_KEY);
        const timer = setTimeout(() => setVisible(true), 1500);
        return () => clearTimeout(timer);
      }
    } catch {
      localStorage.removeItem(CONSENT_KEY);
      setVisible(true);
    }
  }, []);

  const saveConsent = (prefs: ConsentPreferences) => {
    const consent = {
      version: CONSENT_VERSION,
      preferences: prefs,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    applyConsentPreferences(prefs);
    dispatchConsentUpdate(prefs);
    setVisible(false);
  };

  const handleAcceptAll = () => {
    const all = { necessary: true, analytics: true, marketing: true, preferences: true };
    setPreferences(all);
    saveConsent(all);
  };

  const handleRejectAll = () => {
    const minimal = { necessary: true, analytics: false, marketing: false, preferences: false };
    setPreferences(minimal);
    saveConsent(minimal);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
          className="fixed bottom-0 inset-x-0 z-[100] p-4 md:p-6"
        >
          <div className="max-w-4xl mx-auto bg-neutral-900/95 dark:bg-neutral-900/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl shadow-black/50 overflow-hidden">
            {!showDetails ? (
              /* Simple Banner */
              <div className="p-5 md:p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0">
                    <Cookie className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-white text-sm mb-1">We value your privacy</h3>
                    <p className="text-white/60 text-sm leading-relaxed">
                      We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. 
                      As an EU-based service, we comply with GDPR regulations. You can choose which cookies to accept.
                      Read our <Link to="/privacy-policy" className="text-emerald-400 hover:underline">Privacy Policy</Link> and <Link to="/cookie-policy" className="text-emerald-400 hover:underline">Cookie Policy</Link> for more details.
                    </p>
                  </div>
                  <button onClick={handleRejectAll} aria-label="Close cookie banner" className="text-neutral-500 dark:text-white/40 hover:text-neutral-700 dark:hover:text-white/70 transition-colors shrink-0 p-1">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mt-4 ml-14">
                  <Button onClick={handleAcceptAll} className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold h-10 px-6 rounded-lg">
                    Accept All
                  </Button>
                  <Button onClick={handleRejectAll} variant="outline" className="border-white/20 text-white hover:bg-white/10 h-10 px-6 rounded-lg">
                    Reject Non-Essential
                  </Button>
                  <Button onClick={() => setShowDetails(true)} variant="ghost" className="text-white/60 hover:text-white hover:bg-white/5 h-10 px-4 rounded-lg">
                    <Settings className="w-4 h-4 mr-1.5" /> Customize
                  </Button>
                </div>
              </div>
            ) : (
              /* Detailed Preferences */
              <div className="p-5 md:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-white flex items-center gap-2">
                    <Shield className="w-5 h-5 text-emerald-400" /> Cookie Preferences
                  </h3>
                  <button onClick={() => setShowDetails(false)} aria-label="Close cookie preferences modal" className="text-neutral-500 dark:text-white/40 hover:text-neutral-700 dark:hover:text-white/70 transition-colors p-1">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-white/50 text-sm mb-5">
                  Manage your cookie preferences below. Necessary cookies cannot be disabled as they are essential for the website to function.
                </p>

                <div className="space-y-3 mb-6">
                  {/* Necessary */}
                  <div className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10">
                    <div>
                      <div className="font-medium text-sm text-white">Strictly Necessary</div>
                      <div className="text-xs text-neutral-500 dark:text-white/40 mt-0.5">Required for basic website functionality, security, and navigation.</div>
                    </div>
                    <div className="px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-bold">Always On</div>
                  </div>

                  {/* Analytics */}
                  <label className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10 cursor-pointer hover:bg-white/8 transition-colors">
                    <div>
                      <div className="font-medium text-sm text-white">Analytics</div>
                      <div className="text-xs text-neutral-500 dark:text-white/40 mt-0.5">Help us understand how visitors interact with our website (Google Tag Manager).</div>
                    </div>
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) => setPreferences(prev => ({ ...prev, analytics: e.target.checked }))}
                      className="w-5 h-5 rounded border-white/20 bg-black/50 text-emerald-500 focus:ring-emerald-500/50"
                    />
                  </label>

                  {/* Marketing */}
                  <label className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10 cursor-pointer hover:bg-white/8 transition-colors">
                    <div>
                      <div className="font-medium text-sm text-white">Marketing</div>
                      <div className="text-xs text-neutral-500 dark:text-white/40 mt-0.5">Used to deliver relevant advertisements and track campaign effectiveness.</div>
                    </div>
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={(e) => setPreferences(prev => ({ ...prev, marketing: e.target.checked }))}
                      className="w-5 h-5 rounded border-white/20 bg-black/50 text-emerald-500 focus:ring-emerald-500/50"
                    />
                  </label>

                  {/* Preferences */}
                  <label className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10 cursor-pointer hover:bg-white/8 transition-colors">
                    <div>
                      <div className="font-medium text-sm text-white">Functional / Preferences</div>
                      <div className="text-xs text-neutral-500 dark:text-white/40 mt-0.5">Remember your settings like language, region, and theme preferences.</div>
                    </div>
                    <input
                      type="checkbox"
                      checked={preferences.preferences}
                      onChange={(e) => setPreferences(prev => ({ ...prev, preferences: e.target.checked }))}
                      className="w-5 h-5 rounded border-white/20 bg-black/50 text-emerald-500 focus:ring-emerald-500/50"
                    />
                  </label>
                </div>

                <div className="flex flex-col sm:flex-row gap-2">
                  <Button onClick={handleSavePreferences} className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold h-10 px-6 flex-1 rounded-lg">
                    Save My Preferences
                  </Button>
                  <Button onClick={handleAcceptAll} variant="outline" className="border-white/20 text-white hover:bg-white/10 h-10 px-6 rounded-lg">
                    Accept All
                  </Button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
